// client/src/services/productService.ts - FIXED VERSION
import { Product } from '../components/Shop';

const API_BASE_URL = '/api/shop';

export const productService = {
  /**
   * Get all products
   */
  async getAllProducts(): Promise<Product[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/products`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching all products:', error);
      throw error;
    }
  },

  /**
   * Get featured products
   */
  async getFeaturedProducts(): Promise<Product[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/products/featured`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching featured products:', error);
      throw error;
    }
  },

  /**
   * Get related products by product ID and category
   */
  async getRelatedProducts(currentProductId: string, category: string, limit: number = 5): Promise<Product[]> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/products/related/${encodeURIComponent(currentProductId)}?category=${encodeURIComponent(category)}&limit=${limit}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success && Array.isArray(data.products)) {
        return data.products;
      }

      console.warn('Unexpected related products response structure:', data);
      return [];
    } catch (error) {
      console.error(`Error fetching related products for ${currentProductId}:`, error);
      return []; // Return empty array on error instead of throwing
    }
  },

  /**
   * Get products by category
   */
  async getProductsByCategory(categoryId: string): Promise<Product[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/products/category/${encodeURIComponent(categoryId)}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error(`Error fetching products for category ${categoryId}:`, error);
      throw error;
    }
  },

  /**
   * Get product by ID - FIXED: Now stores in localStorage when product is retrieved
   */
  async getProductById(id: string | number): Promise<Product | null> {
    try {
      const productId = typeof id === 'number' ? id.toString() : id;
      const response = await fetch(`${API_BASE_URL}/products/${encodeURIComponent(productId)}`);

      if (!response.ok) {
        if (response.status === 404) {
          console.warn(`Product with ID ${productId} not found`);
          return null;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const product = await response.json();

      // Store the complete product in localStorage when fetched
      if (product && product.id) {
        this.visitedProducts.addVisited(product);
      }

      return product;
    } catch (error) {
      console.error(`Error fetching product ${id}:`, error);
      if (error instanceof TypeError || (error as any).name === 'TypeError') {
        return null;
      }
      throw error;
    }
  },

  /**
   * Create a new product
   */
  async createProduct(productData: Omit<Product, 'id' | 'images'>): Promise<Product> {
    try {
      const response = await fetch(`${API_BASE_URL}/products`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(productData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating product:', error);
      throw error;
    }
  },

  /**
   * Update a product
   */
  async updateProduct(id: number, productData: Partial<Product>): Promise<Product> {
    try {
      const response = await fetch(`${API_BASE_URL}/products/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(productData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Error updating product ${id}:`, error);
      throw error;
    }
  },

  /**
   * Delete a product
   */
  async deleteProduct(id: number): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/products/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error(`Error deleting product ${id}:`, error);
      throw error;
    }
  },

  /**
   * Add an image URL to a product
   */
  async addProductImageUrl(productId: number, url: string, alt: string, isMain: boolean = false): Promise<any> {
    try {
      const response = await fetch(`${API_BASE_URL}/products/image-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          productId,
          url,
          alt,
          isMain
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error adding image URL to product:', error);
      throw error;
    }
  },

  /**
   * Delete a product image
   */
  async deleteProductImage(imageId: number): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/products/image/${imageId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error(`Error deleting image ${imageId}:`, error);
      throw error;
    }
  },

  /**
   * Get available age ranges
   */
  async getAgeRanges(): Promise<string[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/age-ranges`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success && Array.isArray(data.ageRanges)) {
        return data.ageRanges;
      }

      console.warn('Unexpected age ranges response structure:', data);
      return [
        '3+ years', '4+ years', '5+ years', '6+ years', '8+ years',
        '8-10 years', '9+ years', '10+ years', '12+ years', '14+ years'
      ];
    } catch (error) {
      console.error('Error fetching age ranges:', error);
      return [
        '3+ years', '4+ years', '5+ years', '6+ years', '8+ years',
        '8-10 years', '9+ years', '10+ years', '12+ years', '14+ years'
      ];
    }
  },

  /**
   * Get products filtered by age range
   */
  async getProductsByAgeRange(ageRange: string, category?: string): Promise<Product[]> {
    try {
      let url = `${API_BASE_URL}/age-ranges/products/${encodeURIComponent(ageRange)}`;

      if (category) {
        url += `?category=${encodeURIComponent(category)}`;
      }

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success && Array.isArray(data.products)) {
        console.log(`Found ${data.products.length} products for age range ${ageRange}${category ? ` in category ${category}` : ''}`);
        return data.products;
      }

      console.warn('Unexpected products response structure:', data);
      return [];
    } catch (error) {
      console.error(`Error fetching products for age range ${ageRange}:`, error);
      return [];
    }
  },

  /**
   * Search products by term
   */
  async searchProducts(searchTerm: string): Promise<Product[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/products/search?q=${encodeURIComponent(searchTerm)}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error(`Error searching products with term "${searchTerm}":`, error);
      throw error;
    }
  },

  /**
   * FIXED: Visited products localStorage management - NO SERVER CALLS
   */
  visitedProducts: {
    /**
     * Get visited products from localStorage - RETURNS FULL PRODUCT OBJECTS
     */
    getVisitedProducts(): Product[] {
      try {
        const stored = localStorage.getItem('lastVisitedProducts');
        if (!stored) return [];

        const products = JSON.parse(stored);
        return Array.isArray(products) ? products.slice(0, 5) : [];
      } catch (error) {
        console.error('Error parsing visited products from localStorage:', error);
        localStorage.removeItem('lastVisitedProducts');
        return [];
      }
    },

    /**
     * Add a complete product to visited items - STORES FULL PRODUCT OBJECT
     */
    addVisited(product: Product): void {
      try {
        const visited = this.getVisitedProducts();

        // Remove existing product with same ID to avoid duplicates
        const filteredVisited = visited.filter(item => item.id !== product.id);

        // Add current product to the beginning and keep only last 5
        const newVisited = [product, ...filteredVisited].slice(0, 5);

        localStorage.setItem('lastVisitedProducts', JSON.stringify(newVisited));
      } catch (error) {
        console.error('Error saving visited product to localStorage:', error);
      }
    },

    /**
     * Remove a product from visited items by ID
     */
    removeVisited(productId: number): void {
      try {
        const visited = this.getVisitedProducts();
        const filteredVisited = visited.filter(item => item.id !== productId);
        localStorage.setItem('lastVisitedProducts', JSON.stringify(filteredVisited));
      } catch (error) {
        console.error('Error removing product from visited items:', error);
      }
    },

    /**
     * Clear all visited items
     */
    clearVisited(): void {
      try {
        localStorage.removeItem('lastVisitedProducts');
      } catch (error) {
        console.error('Error clearing visited items:', error);
      }
    }
  }
};